import type { ReactNode } from 'react';
import Overview from '../DashboardModules/Overview/Overview';
import ManagementProfileTab from '../DashboardModules/ManagementProfileTab/ManagementProfileTab';
import Financials from '../DashboardModules/Financials/Financials';
import { isMobileSectionId } from './constants';
import type { MobileCompanySectionContext, MobileSectionId } from './types';

export type MobileCompanySection = {
    key: string;
    label: string;
    permission?: string;
    render: (ctx: MobileCompanySectionContext) => ReactNode;
};

// The subset of a desktop `dashboardConfig.tabs` (AICA_DASHBOARD config, see
// Dashboard/index.tsx `visibleTabs`) entry mobile cares about.
type DashboardConfigTab = {
    id: string;
    label?: string;
    showInSidebar?: boolean;
};

// Renderers for sections mobile knows how to display, keyed by the same tab
// `id` desktop's dashboardConfig.tabs uses. Adding a new mobile section is
// registering a renderer here — no restructuring of Dashboard.
const MOBILE_SECTION_RENDERERS: Record<MobileSectionId, MobileCompanySection['render']> = {
    overview: ({ selectedCompany, refetchOverviewRef, appendSearchParams, navigateToTab, triggerMain }) => (
        <Overview
            selectedCompany={selectedCompany}
            refetchOverviewRef={refetchOverviewRef}
            appendSearchParams={appendSearchParams}
            navigateToTab={navigateToTab}
            triggerMain={triggerMain}
        />
    ),
    // The real desktop tab, not a mobile copy — its header reflows via the [data-mobile-shell] rules in its own stylesheet.
    managementProfile: ({ navigateToTab, mcaDocData }) => <ManagementProfileTab navigateToTab={navigateToTab} mcaDocData={mcaDocData} />,
    auditedFinancials: ({ selectedCompany }) => <Financials companyName={selectedCompany?.orgName || ''} />,
};

// Optional entitlement gate per section (mirrors the existing ACCESS_TIERS
// gating applied in Dashboard's `sectionPermitted`) — independent of the
// desktop tab config, since dashboardConfig.tabs carries no per-tab
// entitlement field today.
const MOBILE_SECTION_PERMISSIONS: Record<MobileSectionId, string | undefined> = {
    overview: undefined,
    managementProfile: undefined,
    auditedFinancials: undefined,
};

// Used before dashboardConfig.tabs has loaded (or if it doesn't contain a tab
// mobile knows how to render) so Overview keeps showing immediately, exactly
// as it does today, instead of blanking out mid-fetch.
const DEFAULT_TABS: DashboardConfigTab[] = [{ id: 'overview', label: 'Overview' }];

// Derives the mobile section list from the SAME dashboardConfig.tabs desktop's
// sidebar (`visibleTabs`) is built from — single source of truth for tab
// visibility/order. Only tabs mobile has a registered renderer for are
// included, so this can never surface a desktop-only tab on mobile.
export function getMobileCompanySections(desktopVisibleTabs?: DashboardConfigTab[] | null): MobileCompanySection[] {
    const tabs = desktopVisibleTabs?.length ? desktopVisibleTabs : DEFAULT_TABS;
    return tabs
        .filter((tab): tab is DashboardConfigTab & { id: MobileSectionId } => isMobileSectionId(tab.id))
        .map((tab) => ({
            key: tab.id,
            label: tab.label ?? tab.id,
            permission: MOBILE_SECTION_PERMISSIONS[tab.id],
            render: MOBILE_SECTION_RENDERERS[tab.id],
        }));
}
